// Importiert aus https://gitlab.pooliestudios.com/koeln-marathon/app/app-dev
import parseISO from 'date-fns/parseISO';
import addMinutes from 'date-fns/addMinutes';
import differenceInDays from 'date-fns/differenceInDays';
import {RunInterface, Runner} from '../models/runner';
import HumanReadableTime from '../models/HumanReadableTime';
import {RUN_FULL, RUN_HALF, RUN_RELAY} from '../models/waypoint';

export type RaceEvent = {year: number, date: Date, start?: Record<string, Date>, end?: Record<string, Date>};

export const LANGUAGE_DE = 'DE';
export const LANGUAGE_EN = 'EN';

export default class RunProvider {
    static instance = null;

    // Die Uhrzeiten sind bisher identisch, deshalb Generierung im Constructor basierend auf dem jeweiligen Datum
    events: Array<RaceEvent> = [
        {
            year: 2018,
            date: parseISO('2018-10-07T00:00:00+0200'),
        },
        {
            year: 2019,
            date: parseISO('2019-10-13T00:00:00+0200'),
        },
        {
            year: 2023,
            date: parseISO('2023-10-01T00:00:00+0200'),
        },
    ];

    private language: string;

    static getInstance(): RunProvider {
        if (!this.instance) {
            this.instance = new RunProvider();
        }

        return this.instance;
    }

    constructor() {
        this.events = this.events.map((event): RaceEvent => {
            const start = {};
            // Halbmarathon startet um 9:00 Uhr
            start[RUN_HALF] = addMinutes(event.date, 9 * 60);
            // Marathon/Staffelmarathon startet um 10:30 Uhr
            start[RUN_FULL] = addMinutes(event.date, 10.5 * 60);
            start[RUN_RELAY] = addMinutes(event.date, 10.5 * 60);

            const end = {};
            // Halbmarathon endet nach 3,5 h
            end[RUN_HALF] = addMinutes(start[RUN_HALF], 3.5 * 60);
            // Marathon/Staffelmarathon endet nach 6,5 h
            end[RUN_FULL] = addMinutes(start[RUN_FULL], 6.5 * 60);
            end[RUN_RELAY] = addMinutes(start[RUN_RELAY], 6.5 * 60);

            /**
             * @returns {Event}
             */
            return {
                ...event,
                start,
                end,
            };
        });
    }

    getLanguage() {
        return this.language;
    }

    setLanguage(language: string) {
        this.language = language;

        return this;
    }

    translate(originalString: string, value?: string): string {
        const translations = {
            '~ Uhrzeit': {[LANGUAGE_EN]: '~ Time'},
            '~ Zielzeit': {[LANGUAGE_EN]: '~ Finish time'},
            'Daten werden geladen...': {[LANGUAGE_EN]: 'Loading data...'},
            'Aufgepasst! Es geht los in:': {[LANGUAGE_EN]: 'Get ready! The start will be in:'},
            'Der %s startet in:': {[LANGUAGE_EN]: 'The %s will start in:'},
            Tag: {[LANGUAGE_EN]: 'Day'},
            Marathon: {[LANGUAGE_EN]: 'Marathon'},
            Halbmarathon: {[LANGUAGE_EN]: 'Half marathon'},
            Staffelmarathon: {[LANGUAGE_EN]: 'Relay marathon'},
        };

        const withReplacements = (string: string) => {
            if (!value) return string;
            return string.replace('%s', value);
        };

        if (
            this.getLanguage() === LANGUAGE_DE
            || !Object.hasOwnProperty.call(translations, originalString)
        ) {
            return withReplacements(originalString);
        }

        return withReplacements(translations[originalString][this.getLanguage()]);
    }

    getStart(run: RunInterface): Date {
        return this.events.find((event) => event.year === run.year).start[run.run];
    }

    getEnd(run: RunInterface): Date {
        return this.events.find((event) => event.year === run.year).end[run.run];
    }

    getYears(): Array<number> {
        return this.events.map((event) => event.year);
    }

    getLatestYear(): number {
        return this.events[this.events.length - 1].year;
    }

    // Letzer stattgefundener Lauf (heute eingeschlossen)
    getPreviousYear(): number {
        const reversedEvents = this.events.slice()
            .reverse();

        const prevEvent = reversedEvents.find((event) => differenceInDays(event.date, new Date()) <= 0);

        if (prevEvent) {
            return prevEvent.year;
        }

        return this.getLatestYear();
    }

    // Nächster stattfindender Lauf (heute eingeschlossen)
    getNextYear(): number {
        const nextEvent = this.events.find((event) => differenceInDays(event.date, new Date()) >= 0);

        if (nextEvent) {
            return nextEvent.year;
        }

        return this.getLatestYear();
    }

    getTimeUntilRun(run: RunInterface): HumanReadableTime {
        const now = new Date().getTime();
        const start = this.getStart(run)
            .getTime();
        const milliseconds = Number(start - now);
        const seconds = Math.round(milliseconds / 1000);
        return new HumanReadableTime(seconds);
    }

    hasRunEnded(runner: Runner): boolean {
        if (runner.hasStarted()) {
            return runner.hasFinished() || runner.isOutOfTime();
        }

        return Number(this.getEnd(runner)
            .getTime() - new Date().getTime()) > 0;
    }
}
